import { z } from 'zod';

/**
 * The type-specific half of a questionnaire field.
 *
 * WHY THIS IS ONE FILE AND ONE TYPE. `QuestionnaireField` on the backend stores this as a
 * single opaque `config` jsonb string and deliberately never parses it (see
 * QuestionnaireField.ConfigJson). That makes THIS file the only definition of the shape in the
 * whole product — the builder writes it, the renderer reads it, and neither can drift from the
 * other because there is nowhere else for the contract to live.
 *
 * Everything here is optional. A Short Text field has no config at all and stores SQL NULL;
 * only the types that need extra structure carry any.
 */

/** One line of an Address field. The designer ships three: unit, building, street. */
export const addressSubfieldSchema = z.object({
  id: z.string(),
  title: z.string(),
  placeholder: z.string(),
});

export type AddressSubfield = z.infer<typeof addressSubfieldSchema>;

/**
 * How deep a cascade may go.
 *
 * Five, because Country → State → District → Taluka → Village is the deepest hierarchy Indian
 * addresses actually use. A cap exists at all so the tree editor cannot be nested into something
 * no phone screen can render, and so a hand-edited config cannot ask the renderer to draw
 * forty selects.
 */
export const MAX_CASCADE_LEVELS = 5;

/**
 * One tier of a cascade, NAMED BY THE ADMIN.
 *
 * The level owns the question ("Country", "State", "District"); the nodes below own the answers.
 * Keeping them apart is what lets a tier be renamed without touching a single option, and what
 * lets the same tier's title appear once instead of being repeated on every branch — which is what
 * the old two-level shape did, where each mapped block carried its own `title` and three branches
 * meant three chances to word the same question differently.
 */
export const cascadeLevelSchema = z.object({
  id: z.string(),
  title: z.string(),
  placeholder: z.string(),
});

export type CascadeLevel = z.infer<typeof cascadeLevelSchema>;

/**
 * One option in the cascade tree, with the options that depend on it.
 *
 * `id` IS THE IDENTITY, NOT `label`. The previous shape linked a dependent list to its parent by
 * matching the parent's text (`mapToOption: "Gujarat"`), so correcting a typo in an option silently
 * detached everything under it. An id survives any amount of relabelling.
 *
 * `label` is what the applicant reads, and — recorded alongside the id in the answer — what the
 * audit record shows they were actually offered.
 */
export interface CascadeNode {
  id: string;
  label: string;
  children?: CascadeNode[];
}

/**
 * Recursive, so depth is DATA rather than schema. The two-level version needed a new field for a
 * third tier; this one needs nothing.
 *
 * `z.lazy` with an explicit type annotation is how Zod expresses a self-referencing shape — it
 * cannot infer the recursion on its own.
 */
export const cascadeNodeSchema: z.ZodType<CascadeNode> = z.lazy(() =>
  z.object({
    id: z.string(),
    label: z.string(),
    children: z.array(cascadeNodeSchema).optional(),
  }),
);

/**
 * A dependent-dropdown chain: Country → State → District, with the tiers named by whoever built it.
 *
 * REPLACES the old `mappedDropdowns`, which could only ever be two levels deep and linked tiers by
 * label. Nothing is lost in the swap: no questionnaire has ever been saved with the old shape (the
 * `config` column does not exist in any database yet), so there is no legacy data to read and no
 * compatibility layer to carry.
 */
export const cascadeSchema = z.object({
  /** Ordered, one per tier. `levels[0]` is the first dropdown the applicant sees. */
  levels: z.array(cascadeLevelSchema).max(MAX_CASCADE_LEVELS),
  /** The roots — the options offered at `levels[0]`. */
  nodes: z.array(cascadeNodeSchema),
});

/**
 * Declared explicitly rather than inferred from the schema above.
 *
 * `z.lazy` erases enough type information that `z.infer` widens `nodes` to possibly-undefined, so
 * every consumer would have to null-check a field the schema requires. Writing the interface keeps
 * the type honest; the schema still validates at the boundary.
 */
export interface Cascade {
  levels: CascadeLevel[];
  nodes: CascadeNode[];
}

export const fieldConfigSchema = z.object({
  /** Drop Down, Single Choice, Multiple Choice, State, District, City. */
  options: z.array(z.string()).optional(),
  /** Address only. */
  addressSubfields: z.array(addressSubfieldSchema).optional(),
  /**
   * Drop Down only. When present, the field is a CHAIN of dropdowns and `options` above is
   * ignored — the first tier's options are `cascade.nodes`.
   */
  cascade: cascadeSchema.optional(),
});

export type FieldConfig = z.infer<typeof fieldConfigSchema>;

export const EMPTY_FIELD_CONFIG: FieldConfig = {};

/**
 * Reads a stored `config` string.
 *
 * TOLERANT BY DESIGN — never throws, and returns an empty config for null, blank, malformed
 * JSON, or a valid-JSON-but-wrong-shape value. A questionnaire written by a future build with a
 * config key this build has never seen must still open in the builder; the alternative is an
 * admin locked out of their own form by a field they cannot see to delete.
 *
 * Unknown keys are dropped rather than preserved. Round-tripping them would be kinder to a
 * newer client, but it would also let this build re-save data it cannot render, so the honest
 * behaviour is to keep only what we understand.
 */
export function parseFieldConfig(json: string | null | undefined): FieldConfig {
  if (!json || json.trim().length === 0) {
    return EMPTY_FIELD_CONFIG;
  }

  try {
    const parsed = fieldConfigSchema.safeParse(JSON.parse(json));
    return parsed.success ? parsed.data : EMPTY_FIELD_CONFIG;
  } catch {
    return EMPTY_FIELD_CONFIG;
  }
}

/**
 * Serialises a config for saving, or null when there is nothing to store.
 *
 * NULL RATHER THAN `"{}"` matters: it keeps the column genuinely empty for the eleven element
 * types that have no configuration, so `config IS NULL` stays a meaningful query and the row
 * does not imply a configuration was authored and left blank.
 */
export function serialiseFieldConfig(config: FieldConfig): string | null {
  const populated = Object.entries(config).filter(([key, value]) => {
    if (value === undefined) return false;
    if (Array.isArray(value) && value.length === 0) return false;
    // A cascade with no tiers and no options is a chain that was started and abandoned. Dropping it
    // keeps the column NULL for a plain dropdown, so `config IS NULL` still means "no
    // configuration" rather than "an empty cascade object was left behind".
    if (key === 'cascade') {
      const cascade = value as Cascade;
      if (cascade.levels.length === 0 && cascade.nodes.length === 0) return false;
    }
    return true;
  });

  return populated.length === 0 ? null : JSON.stringify(Object.fromEntries(populated));
}

/**
 * Node count above which a cascade is big enough to be worth mentioning.
 *
 * NOT A LIMIT — nothing is blocked at this number. It exists because a cascade lives inline in the
 * field's `config` jsonb, which means the WHOLE tree is shipped to every applicant's phone on every
 * render of that step. A few hundred nodes is nothing; full district-and-city data for India is
 * upwards of 6,000 nodes and several hundred kilobytes, and a handset on a weak connection pays for
 * all of it before the first dropdown appears.
 *
 * Two thousand is where that starts to be felt rather than measured. A tree past it is a signal the
 * options belong in a shared, lazily-fetched list rather than in one field's config — but that is a
 * judgement for whoever is building the form, so we say so and let them decide.
 */
export const CASCADE_NODE_WARN_THRESHOLD = 2000;

/** How many options a cascade holds in total, across every tier. */
export function countCascadeNodes(cascade: Cascade): number {
  const walk = (nodes: CascadeNode[]): number =>
    nodes.reduce((sum, node) => sum + 1 + (node.children ? walk(node.children) : 0), 0);
  return walk(cascade.nodes);
}

/** How many options sit at each tier — `[1, 36, 752]` for one country, 36 states, 752 districts. */
export function cascadeNodesPerLevel(cascade: Cascade): number[] {
  const counts: number[] = [];
  let tier = cascade.nodes;
  while (tier.length > 0) {
    counts.push(tier.length);
    tier = tier.flatMap((node) => node.children ?? []);
  }
  return counts;
}

/** A fresh id for a level or a node. Prefixed so a stray id is recognisable in stored JSON. */
export function newCascadeId(prefix: 'lv' | 'n'): string {
  return `${prefix}_${crypto.randomUUID().slice(0, 8)}`;
}

/**
 * The nodes offered at one tier, given the ids chosen above it.
 *
 * Walks the tree following `path`; returns an empty list when the path does not resolve, which
 * happens legitimately — an admin can delete a branch an applicant had already selected into, and
 * the renderer then shows no options rather than the wrong ones.
 */
export function cascadeNodesAt(cascade: Cascade, path: readonly string[]): CascadeNode[] {
  let level = cascade.nodes;
  for (const id of path) {
    const node = level.find((n) => n.id === id);
    if (!node) return [];
    level = node.children ?? [];
  }
  return level;
}

/** Finds a node by the ids leading to it, or null. */
export function cascadeNodeAt(cascade: Cascade, path: readonly string[]): CascadeNode | null {
  let level = cascade.nodes;
  let found: CascadeNode | null = null;
  for (const id of path) {
    const node = level.find((n) => n.id === id);
    if (!node) return null;
    found = node;
    level = node.children ?? [];
  }
  return found;
}

/**
 * Applies `update` to the node at `path` and returns a NEW tree.
 *
 * Immutable so React sees a changed reference, and one function rather than a bespoke splice at each
 * call site in the editor — every add, rename and delete goes through here, so there is one place
 * for the tree-walking to be wrong.
 *
 * `update` receives the node's sibling list and returns the replacement list, which is what makes
 * the same helper serve insertion and removal as well as editing.
 */
export function updateCascadeNodes(
  nodes: CascadeNode[],
  path: readonly string[],
  update: (siblings: CascadeNode[]) => CascadeNode[],
): CascadeNode[] {
  if (path.length === 0) {
    return update(nodes);
  }

  const [head, ...rest] = path;
  return nodes.map((node) =>
    node.id === head
      ? { ...node, children: updateCascadeNodes(node.children ?? [], rest, update) }
      : node,
  );
}
